import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How GTBuy Spreadsheet Improves Ecommerce Profit Margins",
  description: "Discover how structured sourcing with GTBuy Spreadsheet directly impacts your bottom line. Data-driven methods to cut supplier costs and boost per-unit profitability.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/improve-profit-margins`,
  },
};

export default function ImproveProfitMargins() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Strategy</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Strategy &middot; 7 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">How GTBuy Spreadsheet Improves Ecommerce Profit Margins</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">Discover how structured sourcing with GTBuy Spreadsheet directly impacts your bottom line. Data-driven methods to cut supplier costs and boost per-unit profitability across your entire catalog.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Financial%20profit%20growth%20visualization%20with%20laptop%20displaying%20ascending%20green%20revenue%20charts%20and%20margin%20analysis%20spreadsheet%20alongside%20calculator%20and%20stacked%20coins%20on%20clean%20white%20desk%20warm%20optimistic%20sunlight%20business%20success%20photography%20bright%20positive%20atmosphere%20high%20detail&width=1600&height=700&seq=article-profit-margins-header&orientation=landscape"
                alt="Improve Ecommerce Profit Margins" title="GTBuy Spreadsheet Improves Profit Margins" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">The Direct Link Between Sourcing and Profit</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Most ecommerce sellers focus on revenue growth — more traffic, more conversions, higher average order value. But <strong>profit margin improvement through smarter sourcing</strong> often delivers a bigger bottom-line impact with less effort. Reducing your per-unit cost by just 5% can increase net profit more than a 20% boost in sales volume.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-8">The GTBuy Spreadsheet system targets exactly this leverage point by transforming how you compare, negotiate, and track supplier pricing.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">Profit Impact of Each Strategy</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tl-lg">Sourcing Strategy</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tr-lg">Estimated Profit Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Systematic supplier comparison (3+ quotes per product)", "+12-18% profit"],
                    ["Historical price trend tracking and seasonal buying", "+8-12% profit"],
                    ["Bulk order consolidation across suppliers", "+5-10% profit"],
                    ["Automated MOQ optimization", "+3-7% profit"],
                    ["Supplier performance scoring and rotation", "+4-8% profit"],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">{row[0]}</td>
                      <td className="px-4 py-3 text-sm text-foreground-700 font-semibold text-accent-700 border-b border-background-200">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Why Structured Sourcing Beats Gut Feeling</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">The most expensive mistake in ecommerce sourcing is making decisions based on <strong>incomplete information</strong>. When you only have two supplier quotes stored in separate email threads, you cannot possibly know if you are getting the best deal. The GTBuy system eliminates this blind spot by enforcing a minimum of three comparable quotes for every product, stored in a standardized format that makes price differences immediately visible.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Sellers who adopt this disciplined approach consistently report margin improvements of 10-20% within the first quarter. The system does not require you to be a better negotiator — it simply ensures you never overpay because of disorganized data.</p>

            <p className="text-sm text-foreground-500 italic mb-4">
              Related guides:{" "}
              <Link to="/reduce-sourcing-costs" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Reduce Sourcing Costs</Link>{" "}&middot;{" "}
              <Link to="/how-to-use-gtbuy" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">How to Use GTBuy</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Start Improving Your Margins</h2>
            <p className="text-foreground-400 text-sm mb-6">Explore products across 14 categories on KA Spreadsheet and apply the structured sourcing methodology.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">GTBuy Sheet — Shop Now</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}